// Deal Profile list + editor views (Phase 5C).
//
// List: hydrates from svc.dealProfile.list(), renders a card per profile
// with quick actions (open, duplicate, set default, delete). Click a
// card → /deals/profiles/<slug>/.
//
// Editor: hydrates the named profile (or template for `new`), renders
// grouped sections (Mode, Acquisition band, Property criteria, Cost
// assumptions, Finance assumptions, Target metrics), and debounce-
// persists changes. Saving the name field is required to create the row
// the first time (POST /api/deal-profiles/); subsequent edits PUT.

import {svc} from '../../request';

const STANDARD_SAVE_DEBOUNCE_MS = 500;
const PROFILE_MODE_LABELS: Record<DealProfileMode, string> = {
	reno: 'Renovation / Flip',
	build: 'New Build',
	either: 'Either / Both',
};

// ─────────────────────── List view ───────────────────────

export class DealProfileListView {
	private bodyEl: HTMLElement | null;
	private emptyEl: HTMLElement | null;

	constructor() {
		this.bodyEl = document.getElementById('id_lb-deal-profile-list-body');
		this.emptyEl = document.getElementById('id_lb-deal-profile-list-empty');
		void this.hydrate();
	}

	private async hydrate(): Promise<void> {
		try {
			const rv = await svc.dealProfile.list();
			this.render(rv.profiles);
		} catch {
			this.render(null);
		}
	}

	private render(profiles: ReadonlyArray<IDealProfile> | null): void {
		if (!this.bodyEl) {
			return;
		}
		while (this.bodyEl.firstChild) {
			this.bodyEl.removeChild(this.bodyEl.firstChild);
		}
		if (this.emptyEl && this.emptyEl.parentNode) {
			this.emptyEl.parentNode.removeChild(this.emptyEl);
		}
		if (!profiles || profiles.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-deal-profile-list__empty';
			const title = document.createElement('strong');
			title.textContent = 'No profiles yet.';
			const body = document.createElement('span');
			body.textContent = 'A profile captures your underwriting thesis — price range, sqft criteria, cost basis, target ROI — so you can re-apply it to many parcels without re-typing.';
			empty.appendChild(title);
			empty.appendChild(body);
			const cta = document.createElement('a');
			cta.href = '/deals/profiles/onboarding/';
			cta.className = 'lb-deal-profile-list__empty-cta';
			cta.textContent = 'Quick start (5 questions)';
			empty.appendChild(cta);
			this.bodyEl.appendChild(empty);
			return;
		}
		const list = document.createElement('ul');
		list.className = 'lb-deal-profile-list__items';
		profiles.forEach(p => {
			list.appendChild(this.renderItem(p));
		});
		this.bodyEl.appendChild(list);
	}

	private renderItem(profile: IDealProfile): HTMLElement {
		const li = document.createElement('li');
		li.className = 'lb-deal-profile-list__item';
		const link = document.createElement('a');
		link.href = `/deals/profiles/${encodeURIComponent(profile.slug)}/`;
		link.className = 'lb-deal-profile-list__link';

		const titleRow = document.createElement('div');
		titleRow.className = 'lb-deal-profile-list__title-row';
		const title = document.createElement('span');
		title.className = 'lb-deal-profile-list__title';
		title.textContent = profile.name || profile.slug;
		titleRow.appendChild(title);
		if (profile.isDefault) {
			const chip = document.createElement('span');
			chip.className = 'lb-deal-profile-list__default-chip';
			chip.textContent = 'Default';
			titleRow.appendChild(chip);
		}
		link.appendChild(titleRow);

		const meta = document.createElement('span');
		meta.className = 'lb-deal-profile-list__meta';
		meta.textContent = `${PROFILE_MODE_LABELS[profile.mode] || profile.mode} · ` +
			`${formatBand(profile.minPrice, profile.maxPrice, formatCurrencyBand)} · ` +
			`${formatBand(profile.minSqft, profile.maxSqft, formatNumberBand)} sqft · ` +
			`target ${profile.targetRoiPct.toFixed(0)}% ROI`;
		link.appendChild(meta);
		li.appendChild(link);

		// "Start deal" CTA — entry point for the Phase 5C profile→deal
		// chain. Stops event propagation so clicking it doesn't also
		// follow the parent anchor's href.
		const actions = document.createElement('div');
		actions.className = 'lb-deal-profile-list__actions-row';
		const startDeal = document.createElement('a');
		startDeal.href = `/deals/new/?profile=${encodeURIComponent(profile.slug)}`;
		startDeal.className = 'lb-deal-profile-list__action';
		startDeal.textContent = 'Start deal';
		startDeal.addEventListener('click', evt => evt.stopPropagation());
		actions.appendChild(startDeal);
		li.appendChild(actions);
		return li;
	}
}

// ─────────────────────── Editor view ───────────────────────

export class DealProfileEditorView {
	private rootEl: HTMLElement | null;
	private nameInput: HTMLInputElement | null;
	private bodyEl: HTMLElement | null;
	private actionsEl: HTMLElement | null;
	private summaryEl: HTMLElement | null;
	private profile: IDealProfile | null = null;
	private template: IDealProfile | null = null;
	private state: INewDealProfile = {};
	private writerTimer: number | null = null;

	constructor() {
		this.rootEl = document.getElementById('id_lb-deal-profile-editor');
		this.nameInput = document.getElementById('id_lb-deal-profile-name') as HTMLInputElement | null;
		this.bodyEl = document.getElementById('id_lb-deal-profile-editor-body');
		this.actionsEl = document.getElementById('id_lb-deal-profile-editor-actions');
		this.summaryEl = document.getElementById('id_lb-deal-profile-editor-summary');
		void this.hydrate();
	}

	private resolveSlug(): string | null {
		const v = this.rootEl && this.rootEl.getAttribute('data-profile-slug');
		return v && v.trim().length > 0 ? v : null;
	}

	private async hydrate(): Promise<void> {
		const slug = this.resolveSlug();
		if (slug) {
			try {
				this.profile = await svc.dealProfile.get(slug);
				this.state = this.profileToState(this.profile);
			} catch {
				this.profile = null;
			}
		} else {
			// New-profile path: fetch the template from /api/deal-profiles/
			// so frontend doesn't duplicate defaults.
			try {
				const rv = await svc.dealProfile.list();
				this.template = rv.template;
				this.state = this.profileToState(rv.template);
			} catch {
				// Persistence unreachable — fall back to a minimal stub.
				this.state = {mode: 'reno', name: ''};
			}
		}
		this.render();
		this.bindHeader();
	}

	private profileToState(p: IDealProfile): INewDealProfile {
		return {
			name: p.name,
			mode: p.mode,
			minPrice: p.minPrice,
			maxPrice: p.maxPrice,
			minLotAcres: p.minLotAcres,
			maxLotAcres: p.maxLotAcres,
			minSqft: p.minSqft,
			maxSqft: p.maxSqft,
			minYearBuilt: p.minYearBuilt,
			minBeds: p.minBeds,
			minBaths: p.minBaths,
			renoCostPerSqft: p.renoCostPerSqft,
			buildCostPerSqft: p.buildCostPerSqft,
			contingencyPct: p.contingencyPct,
			holdingMonths: p.holdingMonths,
			leveragePct: p.leveragePct,
			financeRatePct: p.financeRatePct,
			targetRoiPct: p.targetRoiPct,
			targetMarginPct: p.targetMarginPct,
			maxLtcPct: p.maxLtcPct,
			maxLtvPct: p.maxLtvPct,
			isDefault: p.isDefault,
		};
	}

	private bindHeader(): void {
		if (!this.nameInput) {
			return;
		}
		this.nameInput.value = this.state.name || '';
		this.nameInput.addEventListener('input', () => {
			this.state.name = this.nameInput!.value;
			this.scheduleSave();
		});
	}

	private render(): void {
		if (!this.bodyEl) {
			return;
		}
		while (this.bodyEl.firstChild) {
			this.bodyEl.removeChild(this.bodyEl.firstChild);
		}
		this.bodyEl.appendChild(this.section('Mode', [
			this.modeRow(),
		]));
		this.bodyEl.appendChild(this.section('Acquisition band', [
			this.bandRow('Price ($)', 'minPrice', 'maxPrice', 'integer'),
			this.bandRow('Lot size (acres)', 'minLotAcres', 'maxLotAcres', 'decimal'),
			this.bandRow('Sqft', 'minSqft', 'maxSqft', 'integer'),
		]));
		this.bodyEl.appendChild(this.section('Property criteria', [
			this.numberRow('Year built (min)', 'minYearBuilt', 'integer'),
			this.numberRow('Beds (min)', 'minBeds', 'integer'),
			this.numberRow('Baths (min)', 'minBaths', 'decimal'),
		]));
		this.bodyEl.appendChild(this.section('Cost assumptions', [
			this.numberRow('Reno $/sqft', 'renoCostPerSqft', 'decimal'),
			this.numberRow('Build $/sqft', 'buildCostPerSqft', 'decimal'),
			this.numberRow('Contingency %', 'contingencyPct', 'decimal'),
			this.numberRow('Holding months', 'holdingMonths', 'decimal'),
		]));
		this.bodyEl.appendChild(this.section('Finance assumptions', [
			this.numberRow('Leverage % (0 = cash)', 'leveragePct', 'decimal'),
			this.numberRow('Rate %', 'financeRatePct', 'decimal'),
		]));
		this.bodyEl.appendChild(this.section('Target metrics', [
			this.numberRow('Target ROI %', 'targetRoiPct', 'decimal'),
			this.numberRow('Target margin %', 'targetMarginPct', 'decimal'),
			this.numberRow('Max LTC %', 'maxLtcPct', 'decimal'),
			this.numberRow('Max LTV %', 'maxLtvPct', 'decimal'),
		]));
		this.renderActions();
	}

	private section(title: string, rows: Array<HTMLElement>): HTMLElement {
		const section = document.createElement('section');
		section.className = 'lb-deal-profile-section';
		const head = document.createElement('header');
		head.className = 'lb-deal-profile-section__head';
		const h = document.createElement('h2');
		h.textContent = title;
		head.appendChild(h);
		section.appendChild(head);
		rows.forEach(r => section.appendChild(r));
		return section;
	}

	private modeRow(): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-profile-row lb-deal-profile-row--mode';
		(['reno', 'build', 'either'] as DealProfileMode[]).forEach(mode => {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'lb-deal-profile-mode-btn';
			if (this.state.mode === mode) {
				btn.classList.add('lb-deal-profile-mode-btn--active');
			}
			btn.textContent = PROFILE_MODE_LABELS[mode];
			btn.addEventListener('click', () => {
				this.state.mode = mode;
				this.render();
				this.scheduleSave();
			});
			row.appendChild(btn);
		});
		return row;
	}

	private bandRow(
		label: string,
		minField: keyof INewDealProfile,
		maxField: keyof INewDealProfile,
		kind: 'integer' | 'decimal',
	): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-profile-row lb-deal-profile-row--band';
		const lab = document.createElement('label');
		lab.className = 'lb-deal-profile-row__label';
		lab.textContent = label;
		row.appendChild(lab);
		row.appendChild(this.numberInput(minField, kind, 'min'));
		const sep = document.createElement('span');
		sep.className = 'lb-deal-profile-row__sep';
		sep.textContent = '–';
		row.appendChild(sep);
		row.appendChild(this.numberInput(maxField, kind, 'max'));
		return row;
	}

	private numberRow(
		label: string,
		field: keyof INewDealProfile,
		kind: 'integer' | 'decimal',
	): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-profile-row';
		const lab = document.createElement('label');
		lab.className = 'lb-deal-profile-row__label';
		lab.textContent = label;
		row.appendChild(lab);
		row.appendChild(this.numberInput(field, kind));
		return row;
	}

	private numberInput(
		field: keyof INewDealProfile,
		kind: 'integer' | 'decimal',
		placeholder: string = '',
	): HTMLInputElement {
		const input = document.createElement('input');
		input.type = 'number';
		input.className = 'lb-deal-profile-row__input';
		input.placeholder = placeholder;
		input.step = kind === 'integer' ? '1' : 'any';
		const v = this.state[field];
		input.value = v === null || v === undefined ? '' : String(v);
		input.addEventListener('input', () => {
			const raw = input.value.trim();
			const next: number | null = raw === '' ? null : Number(raw);
			// Numeric coercion catches NaN — keep last valid state.
			if (next === null || Number.isFinite(next)) {
				(this.state as any)[field] = next;
				this.scheduleSave();
			}
		});
		return input;
	}

	private renderActions(): void {
		if (!this.actionsEl) {
			return;
		}
		while (this.actionsEl.firstChild) {
			this.actionsEl.removeChild(this.actionsEl.firstChild);
		}
		if (this.profile) {
			const setDefault = document.createElement('button');
			setDefault.type = 'button';
			setDefault.className = 'lb-deal-profile-editor__action';
			setDefault.textContent = this.state.isDefault ? 'Default profile' : 'Make default';
			setDefault.disabled = !!this.state.isDefault;
			setDefault.addEventListener('click', () => {
				this.state.isDefault = true;
				this.scheduleSave(true);
				this.renderActions();
			});
			this.actionsEl.appendChild(setDefault);
			const del = document.createElement('button');
			del.type = 'button';
			del.className = 'lb-deal-profile-editor__action lb-deal-profile-editor__action--danger';
			del.textContent = 'Delete';
			del.addEventListener('click', () => this.handleDelete());
			this.actionsEl.appendChild(del);
		}
	}

	private async handleDelete(): Promise<void> {
		if (!this.profile) {
			return;
		}
		if (!window.confirm(`Delete profile "${this.profile.name}"? This cannot be undone.`)) {
			return;
		}
		try {
			await svc.dealProfile.delete(this.profile.slug);
			window.location.assign('/deals/profiles/');
		} catch {
			// Surface failure inline once we have a status surface; for
			// now the editor stays put.
		}
	}

	private scheduleSave(force: boolean = false): void {
		if (this.writerTimer !== null) {
			window.clearTimeout(this.writerTimer);
			this.writerTimer = null;
		}
		const fire = () => {
			this.writerTimer = null;
			void this.flushSave();
		};
		if (force) {
			fire();
		} else {
			this.writerTimer = window.setTimeout(fire, STANDARD_SAVE_DEBOUNCE_MS);
		}
	}

	private async flushSave(): Promise<void> {
		const name = (this.state.name || '').trim();
		// Create path: don't POST until the user has at least typed a
		// name. Avoids spamming the API with empty rows on field blur.
		if (!this.profile) {
			if (!name) {
				return;
			}
			try {
				this.profile = await svc.dealProfile.create({...this.state, name});
				const target = `/deals/profiles/${encodeURIComponent(this.profile.slug)}/`;
				if (window.location.pathname !== target) {
					window.history.replaceState({}, '', target);
					if (this.rootEl) {
						this.rootEl.setAttribute('data-profile-slug', this.profile.slug);
					}
				}
				this.renderActions();
			} catch {
				// Stay in create mode; user can retry by editing again.
			}
			return;
		}
		try {
			this.profile = await svc.dealProfile.update(this.profile.slug, this.state);
		} catch {
			// Persistence failure — silent to keep the editor responsive.
		}
	}
}

// ─────────────────────── helpers ───────────────────────

function formatBand(
	min: number | null,
	max: number | null,
	fmt: (n: number) => string,
): string {
	if (min == null && max == null) {
		return 'any';
	}
	if (min == null) {
		return `≤ ${fmt(max as number)}`;
	}
	if (max == null) {
		return `≥ ${fmt(min)}`;
	}
	return `${fmt(min)} – ${fmt(max)}`;
}

function formatCurrencyBand(n: number): string {
	if (n >= 1_000_000) {
		return `$${(n / 1_000_000).toFixed(1)}M`;
	}
	if (n >= 1_000) {
		return `$${Math.round(n / 1_000)}K`;
	}
	return `$${n}`;
}

function formatNumberBand(n: number): string {
	return n.toLocaleString('en-US');
}
